import {
  type Bot,
  type Message,
  type MessageCtx,
  MessageListSchema,
  type MessagesHistoryResponse,
  type MessagesSubmitParams,
  type MessagesSubmitResponse,
} from '@/api/schema'
import { dayjs } from '@/api/utils/dayjs'
import { ERROR_CODES } from '@/api/utils/dict'
import { type SessionStore } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { isApiErrorCode, parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import { getBotStorageKey } from '@/app/utils/storage'
import { produce } from 'immer'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { create, type StoreApi, type UseBoundStore } from 'zustand'

const RESTORE_MESSAGES_LIMIT = 250

type LoaderResult = Partial<Message> | undefined | void

type Loader = Promise<LoaderResult> | (() => Promise<LoaderResult>)

type MessageVMParams = {
  loader?: Loader
  ctx?: MessageCtx
}

type MessageVMState = {
  loading?: boolean
}

type MessageVM = {
  id: string
  state: MessageVMState
  params: MessageVMParams
  message: Message
}

type ChatState = {
  id: string
  bot: Bot
  ready: boolean
  processing: boolean
  retryText: string | null
  queue: string[]
  messageIds: string[]
  data: { [k: string]: MessageVM }
  init(bot: Bot, initMessage?: InitMessage): Promise<void>
  reset(): void
  enqueue(
    message: Message,
    params?: MessageVMParams,
    state?: MessageVMState,
  ): [string, Promise<void>]
  enqueueAsync(
    message: Message,
    params?: MessageVMParams,
    state?: MessageVMState,
  ): Promise<string>
  process(): Promise<void>
  restore(): Promise<void>
  save(): Promise<void>
  clean(): Promise<void>
  submit(text: string, ctx?: MessageCtx): Promise<void>
  resolve(): Promise<void>
  setRetryText(retryText: string | null): void
}

type InitMessage = {
  q?: string
  ctx?: MessageCtx
}

export type ChatStore = UseBoundStore<StoreApi<ChatState>>

export const ChatStoreContext = createContext<ChatStore | null>(null)

export function useChat<T>(selector: (state: ChatState) => T) {
  const store = useContext(ChatStoreContext)

  if (!store) {
    throw new Error('ChatProvider is required')
  }

  return store(selector)
}

export const createChatStore = (sessionStore: SessionStore) => {
  let syncedHistoryUserId: string | null = null

  const syncHistory = async () => {
    const user = sessionStore.getState().user
    const userId = user?.id

    if (!userId) {
      syncedHistoryUserId = null
      return
    }

    if (syncedHistoryUserId === userId) return

    const bots: Bot[] = ['zoe', 'milana', 'eva']

    const resp = await api.get<MessagesHistoryResponse>('/messages/history')
      .catch(e => {
        console.error(e)
        return null
      })

    if (!resp) return

    for (const bot of bots) {
      try {
        const messages = MessageListSchema.parse(resp.data.messages[bot])

        if (messages.length === 0) continue

        localStorage.setItem(getBotStorageKey(bot), JSON.stringify(messages))
      }
      catch (e) {
        console.error(e)
      }
    }

    syncedHistoryUserId = userId
  }

  const store = create<ChatState>((set, get) => ({
    bot: 'zoe' as const,
    data: {},
    queue: [],
    messageIds: [],
    processing: false,
    retryText: null,
    id: uuidv4(),
    ready: false,

    async init(bot, initMessage) {
      const { resolve, restore, enqueue } = get()
      set({ bot })

      await restore()

      if (initMessage) {
        const q = initMessage.q?.trim()

        if (q && !hasLastUserMessage(get(), q)) {
          enqueue({
            role: 'user',
            text: q,
          }, {
            ctx: initMessage.ctx,
          })
        }
      }

      await resolve()

      set({ ready: true })
    },

    reset() {
      set({
        ready: false,
        data: {},
        queue: [],
        messageIds: [],
        processing: false,
        retryText: null,
        id: uuidv4(),
      })
    },

    enqueue(message, params = {}, state = {}) {
      const id = uuidv4()

      set(produce((s: ChatState) => {
        s.data[id] = {
          id,
          params,
          state,
          message,
        }
        s.queue.push(id)
      }))

      return [id, get().process()]
    },

    async enqueueAsync(message, params = {}, state = {}) {
      const [id, promise] = get().enqueue(message, params, state)
      await promise
      return id
    },

    async process() {
      const { queue, data, processing, id } = get()

      const messageId = queue.at(0)

      if (!messageId || processing) return

      const message = data[messageId]

      if (!message) return

      set(produce((s: ChatState) => {
        s.processing = true
        s.queue.shift()
        s.messageIds.push(messageId)
      }))

      if (message.params.loader) {
        set(produce((s: ChatState) => {
          s.data[message.id].state.loading = true
        }))

        try {
          const result = await waitLoader(message.params.loader)

          if (result) {
            if (get().id !== id) return
            set(produce((s: ChatState) => {
              s.data[message.id].message = {
                ...s.data[message.id].message,
                ...result,
              }
            }))
          }
        }
        catch (e) {
          console.error(e)
        }
        finally {
          if (get().id !== id) return

          set(produce((s: ChatState) => {
            s.data[message.id].state.loading = false
          }))
        }
      }

      get().save()

      set({ processing: false })

      get().process()
    },

    async restore() {
      const { enqueueAsync, bot } = get()

      let messages: Message[] = []

      await syncHistory()

      try {
        const rawData = localStorage.getItem(getBotStorageKey(bot))
        if (rawData) {
          const data = JSON.parse(rawData)
          messages = MessageListSchema.parse(data).slice(-RESTORE_MESSAGES_LIMIT)
        }
      }
      catch (e) {
        console.error(e)
      }

      await Promise.all(
        messages.map(message => enqueueAsync(message)),
      )
    },

    async save() {
      try {
        const { bot, messageIds, data } = get()
        const messages = messageIds
          .map(id => data[id].message)
          .filter(item => !item.skip)
        const messagesJson = JSON.stringify(messages)
        localStorage.setItem(getBotStorageKey(bot), messagesJson)
      }
      catch (e) {
        console.error(e)
      }
    },

    async clean() {
      const { bot } = get()
      const user = sessionStore.getState().user

      if (user) {
        await api.delete('/messages/history', {
          params: {
            bot,
          },
        })
      }

      localStorage.removeItem(getBotStorageKey(bot))

      get().reset()
      get().init(bot)
    },

    async submit(text, ctx) {
      const { enqueue, resolve } = get()

      enqueue({
        text,
        role: 'user',
      }, {
        ctx,
      })

      resolve()
    },

    async resolve() {
      const session = sessionStore.getState()
      const { bot, data, messageIds, enqueue } = get()

      // нет сообщений
      if (messageIds.length === 0) {
        enqueue({
          role: 'model',
          text: '',
        }, {
          loader: withDelay({
            text: getFirstMessage(session.profile?.userName),
          }),
        })
        return
      }

      // есть сообщения
      if (messageIds.length > 0) {
        const messageId = messageIds.at(-1)!
        const messageVM = data[messageId]

        // сообщение от бота
        if (messageVM.message.role !== 'user') {
          return
        }

        // сообщение от юзера
        const lastMessageIds = get().messageIds.slice(-20)
        const messages: Message[] = lastMessageIds.map(id => data[id].message)

        enqueue({
          role: 'model',
          text: '',
        }, {
          loader: api.post<MessagesSubmitResponse>(
            '/messages',
            {
              bot,
              messages,
              tz: dayjs.tz.guess(),
              ctx: messageVM.params.ctx,
            } satisfies MessagesSubmitParams,
          )
            .then(r => {
              sessionStore.getState().sync(r.data.user, r.data.profile)
              metrikaGoal('chat_get_answer')
              return r.data.message
            })
            .catch(e => {
              if (isApiErrorCode(e, ERROR_CODES.aiEmptyAnswer)) {
                set({ retryText: messageVM.message.text })
              }

              return {
                skip: true,
                text: parseError(e).at(0),
              }
            }),
        })
        return
      }
    },

    setRetryText(retryText) {
      set({ retryText })
    },
  }))

  sessionStore.subscribe((state, prevState) => {
    if (state.user?.id !== prevState.user?.id) {
      syncedHistoryUserId = null
    }
  })

  return store
}

function waitLoader(loader: Loader): Promise<LoaderResult> {
  if (typeof loader === 'function') return loader()
  return loader
}

function hasLastUserMessage(state: ChatState, text: string): boolean {
  const normalizedText = normalizeMessageText(text)

  for (let i = state.messageIds.length - 1; i >= 0; i--) {
    const message = state.data[state.messageIds[i]]?.message

    if (message?.role !== 'user') continue

    return normalizeMessageText(message.text) === normalizedText
  }

  return false
}

function normalizeMessageText(text: string): string {
  return text.trim().replace(/\s+/g, ' ')
}

function withDelay(message: Partial<Message>, ms = 500): Promise<Partial<Message>> {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(message)
    }, ms)
  })
}

const FIRST_MESSAGES_TPLS = [
  'Привет, {{user}}! Как твои дела сегодня? ✨',
  'Привет, {{user}}! ✨ Как твои дела сегодня? Есть ли что-то, что тебя особенно волнует или интересует в этот день? 😊',
  'Привет, {{user}}! Как твои дела? Как чувствуешь себя сегодня? ✨',
  'Приветик, {{user}}! Как твои дела сегодня? ✨ Расскажи, как настроение? 😊',
  'Привет, {{user}}! Как твои дела? Как проходит этот день? ✨',
  'Привет, {{user}}! Как твои дела сегодня? ✨ Расскажи, как настроение, чувствуешь ли эту трансформационную энергию дня? 😊',
  'Привет, {{user}}! ✨ Как настроение сегодня? Рассказывай, как твои дела? 😊',
  'Приветик, {{user}}! 👋 Как твои дела сегодня? Есть что-нибудь интересное, что хотелось бы обсудить? ✨',
  'Приветик, {{user}}! 👋 Как твои дела сегодня? Готова поделиться с тобой энергиями дня. ✨',
]

function getFirstMessage(userName?: string | null) {
  const randomIndex = Math.floor(Math.random() * FIRST_MESSAGES_TPLS.length)
  let template = FIRST_MESSAGES_TPLS[randomIndex]

  if (userName && userName.trim()) {
    return template.replace('{{user}}', userName.trim())
  }

  // Если имени нет — убираем обращение с именем
  return template
    .replace(/,\s*\{\{user\}\}/, '') // убираем ", {{user}}"
    .replace(/\{\{user\}\}!/, '!') // если вдруг без запятой
}

export function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (
        Number(c)
        ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
      ).toString(16))
  }

  // Последний fallback, не криптостойкий. Для UI-id ок, для security/token — нельзя.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
